/**
 * home.js — the only JavaScript the homepage needs.
 *
 * Two custom elements, both self-initialising on DOM insertion so a section
 * re-rendered by the theme editor "just works" (spec §2):
 *
 *   <kosha-hero>  slide chrome ("01 / 0N" + prev/next arrows, or dots) and
 *                 autoplay for the hero. Swiping is native CSS scroll-snap; JS
 *                 never positions a slide by hand except when advancing, so
 *                 the component degrades to a plain swipeable strip if this
 *                 file fails to load. With one slide it returns immediately
 *                 and no chrome is rendered by the section at all.
 *   <kosha-tabs>  ARIA tablist behaviour for the product rails.
 *
 * No dependencies, no build step. Kept under 3KB on purpose.
 *
 * Wrapped in an IIFE and guarded on `customElements.get`: the hero section and
 * the tabbed-products section each load this file, and two <script src> tags
 * with the same URL are fetched once but EXECUTED twice. Top-level `const`
 * would throw "already declared" on the second pass.
 */

(function () {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class KoshaHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hero-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
      // Legacy chrome: "01 / 02" plus prev/next. The hero ships with dots, so
      // these three are null; the handlers below stay guarded so a section
      // that opts back into a counter still works.
      this.counter = this.querySelector('[data-hero-current]');
      this.prev = this.querySelector('[data-hero-prev]');
      this.next = this.querySelector('[data-hero-next]');
      if (!this.track || this.slides.length < 2) return;

      if (this.prev) {
        this.prev.addEventListener('click', () => {
          this.goTo((this.index - 1 + this.slides.length) % this.slides.length);
          this.pause();
        });
      }
      if (this.next) {
        this.next.addEventListener('click', () => {
          this.goTo((this.index + 1) % this.slides.length);
          this.pause();
        });
      }

      this.index = 0;
      // Seat the initial state rather than waiting for the first scroll: it is
      // what puts the off-screen slides out of the tab order on load.
      this.setIndex(0);
      this.interval = Number(this.dataset.interval || 6000);
      this.timer = null;

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.goTo(i);
          this.pause();
        });
      });

      // Autoplay pauses on any signal that the visitor is engaged, and while the
      // section is off-screen or the tab is hidden.
      ['pointerenter', 'focusin'].forEach((evt) => this.addEventListener(evt, () => this.pause()));
      ['pointerleave', 'focusout'].forEach((evt) => this.addEventListener(evt, () => this.play()));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.play();
      });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => (entry.isIntersecting ? this.play() : this.pause()),
          { threshold: 0.35 }
        );
        this.observer.observe(this);
      } else {
        this.play();
      }
    }

    disconnectedCallback() {
      this.pause();
      if (this.observer) this.observer.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        const width = this.track.clientWidth || 1;
        const next = Math.round(this.track.scrollLeft / width);
        if (next !== this.index) this.setIndex(next);
      });
    }

    setIndex(index) {
      this.index = Math.max(0, Math.min(index, this.slides.length - 1));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      if (this.counter) {
        this.counter.textContent = String(this.index + 1).padStart(2, '0');
      }
      this.slides.forEach((slide, i) => {
        // Keep off-screen slides out of the tab order without hiding them from
        // the scroller (which would break the snap positions).
        slide.toggleAttribute('inert', i !== this.index);
      });
    }

    goTo(index) {
      const width = this.track.clientWidth;
      this.track.scrollTo({
        left: width * index,
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
      });
      this.setIndex(index);
    }

    play() {
      if (this.timer || REDUCED_MOTION.matches || this.interval <= 0) return;
      this.timer = setInterval(() => {
        this.goTo((this.index + 1) % this.slides.length);
      }, this.interval);
    }

    pause() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Tabs. The tab controls are real <a href> links to their collections, so
   * with JS off the section is three working links rather than three dead
   * buttons — and only the first panel is server-rendered, which took 86KB of
   * hidden markup out of the homepage document.
   *
   * A deferred panel carries `data-panel-url`, pointing at the Section
   * Rendering API on its own collection:
   *   /collections/<handle>?section_id=<section id>
   * The section answers that request with a single [data-panel-body]. Each
   * panel is fetched at most once; the markup then lives in the DOM.
   * Hovering a tab starts the fetch early, so the switch usually feels
   * instant, and the outgoing panel's height is pinned on the incoming one
   * until the content lands so the page never jumps.
   */
  class KoshaTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      if (this.tabs.length < 2) return;

      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.select(i);
        });
        tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
        tab.addEventListener('pointerenter', () => this.load(tab));
        tab.addEventListener('focus', () => this.load(tab));
      });
    }

    panelFor(tab) {
      return document.getElementById(tab.getAttribute('aria-controls'));
    }

    load(tab) {
      const panel = this.panelFor(tab);
      if (!panel) return Promise.resolve();
      if (panel.pending) return panel.pending;
      const url = panel.dataset.panelUrl;
      if (!url) return Promise.resolve();

      panel.pending = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.text();
        })
        .then((html) => {
          const body = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-panel-body]');
          if (!body) throw new Error('no panel body');
          panel.innerHTML = body.innerHTML;
          delete panel.dataset.panelUrl;
        })
        .catch(() => {
          // Leave data-panel-url in place so the next activation retries, and
          // leave the tab's href working as the escape hatch.
          panel.pending = null;
        });

      return panel.pending;
    }

    onKeydown(event, i) {
      const last = this.tabs.length - 1;
      let next = null;
      if (event.key === 'ArrowRight') next = i === last ? 0 : i + 1;
      else if (event.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = last;
      if (next === null) return;
      event.preventDefault();
      this.select(next);
      this.tabs[next].focus();
    }

    select(index) {
      const target = this.panelFor(this.tabs[index]);
      const outgoing = this.tabs
        .filter((tab) => tab.getAttribute('aria-selected') === 'true')
        .map((tab) => this.panelFor(tab))[0];

      // Pin the height we are leaving onto the panel we are entering, so an
      // empty deferred panel cannot collapse the page under the visitor.
      if (target && outgoing && target.dataset.panelUrl) {
        target.style.minHeight = `${outgoing.offsetHeight}px`;
      }

      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.setAttribute('tabindex', selected ? '0' : '-1');
        const panel = this.panelFor(tab);
        if (panel) panel.toggleAttribute('hidden', !selected);
      });

      this.load(this.tabs[index]).then(() => {
        if (target) target.style.minHeight = '';
      });
    }
  }

  if (!customElements.get('kosha-hero')) customElements.define('kosha-hero', KoshaHero);
  if (!customElements.get('kosha-tabs')) customElements.define('kosha-tabs', KoshaTabs);
})();

/* ---------------------------------------------------------------------------
   TAB RAIL PROGRESS BAR

   The thin line under the tab labels on the owner's live site. It is driven
   from the rail's own scroll position rather than from an index, so it moves
   continuously with a finger instead of jumping a step at a time.

   Written as its own listener rather than inside <kosha-tabs>, because the
   rail is replaced whenever a tab is switched and a listener bound to the old
   rail would be thrown away with it. This one lives on the document.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  /* IT REPORTS THE TAB, NOT THE SCROLL (owner, 2026-09-02: "fix the just in
     and best sellers bar, it should just fill whichever side is selected just
     like it is on live theme").

     It used to track how far along the product rail she had scrolled, which
     meant the bar moved while she swiped photographs and sat at 8% the rest of
     the time — it looked broken because it was answering a question nobody had
     asked. The track is now divided by the number of tabs and the segment
     under the selected one is filled: two tabs, two halves. */
  /* THE FILL IS THE SELECTED LABEL'S OWN BOX, measured.

     It used to be `100 / tabs.length` percent, which is only correct when the
     tabs are equal-width columns — and they are not any more. The row now
     shrink-wraps its labels with a 30px gap, exactly as honeyswim.co and her
     live theme do, so "JUST IN" and "BEST SELLERS" are different widths and a
     half-and-half bar would sit under neither of them.

     Measured against the row's own box rather than `offsetLeft`, because
     `offsetParent` is not guaranteed to be the list. Widths in pixels, so a
     percentage translate (which resolves against the FILL's width, not the
     track's) cannot get it wrong. */
  function paint(panel) {
    var fill = panel.querySelector('[data-tabs-progress-fill]');
    var list = panel.querySelector('[role="tablist"]');
    if (!fill || !list) return;

    var tabs = list.querySelectorAll('[role="tab"]');
    if (!tabs.length) return;

    var current = tabs[0];
    for (var i = 0; i < tabs.length; i++) {
      if (tabs[i].getAttribute('aria-selected') === 'true') current = tabs[i];
    }

    var listBox = list.getBoundingClientRect();
    var tabBox = current.getBoundingClientRect();
    if (!listBox.width) return;

    /* `list.scrollLeft` matters on a phone, where the row can scroll under a
       track that does not. */
    var offset = tabBox.left - listBox.left + list.scrollLeft;

    fill.style.width = tabBox.width + 'px';
    fill.style.transform = 'translateX(' + offset + 'px)';
  }

  function paintAll() {
    Array.prototype.forEach.call(document.querySelectorAll('.home-tabs'), paint);
  }

  /* The tab controls set aria-selected on themselves, so watching the click is
     enough — and a click listener on the document survives the panel being
     re-rendered under it. */
  document.addEventListener('click', function (event) {
    var tab = event.target && event.target.closest && event.target.closest('[role="tab"]');
    if (!tab) return;
    var panel = tab.closest('.home-tabs');
    if (panel) requestAnimationFrame(function () { paint(panel); });
  });

  /* Fonts landing and the viewport changing both move the tabs, and a bar that
     was measured before either is a bar in the wrong place. Now that the fill
     is measured in pixels rather than set as a fraction, both of those have to
     be watched rather than assumed away. */
  function init() {
    paintAll();

    window.addEventListener('resize', paintAll);

    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(paintAll);
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
